import { Modal, ModalButtons } from 'ui/Modal'
import { Button } from 'ui/components/Button'
import { useParams } from 'react-router-dom'
import {
  ApiErrorNotification,
  Notification,
  useNotificationContext,
} from 'ui/components/Notification'
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useGlobalBarcodeListenerContext } from 'components/BarcodeListener/GlobalContext'
import { BarcodeListener, BarcodeListenerProps } from 'components/BarcodeListener'
import QRCode from 'qrcode'
import {
  useAssociateBarcode,
  useAssociatedBarcodesForClient,
  useDisassociateBarcode,
  useGetCustomer,
  useUpdateAssociatedBarcode,
} from 'api/Customers'
import { DateTime } from 'luxon'
import { Dropdown, DropdownDivider, DropdownItem } from 'ui/components/Dropdown'
import {
  CalendarIcon,
  DocumentDownloadIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
  XIcon,
  LinkIcon,
} from '@heroicons/react/solid'
import { PaginatedMiniList } from 'ui/components/PaginatedMiniList'
import { ResourceListItem } from 'ui/components/ResourceListItem'
import { Spinner } from 'ui/components/Spinner'
import { useReactToPrint } from 'react-to-print'
import { ClientAvatar } from './ClientAvatar'
import { Input } from 'ui/components/Form/Input'
import { useCurrentUserCanForCurrentBrand } from 'hooks/UseCurrentUserCan'
import { CUSTOMERS_MANAGE } from 'constants/permissions'
import { useTranslation } from 'react-i18next'
import { Badge } from 'ui/components/Badge'
import { PaymentMethodIcon } from 'ui/components/PaymentMethodIcon'
import { SelectTerminalModal } from './SelectTerminalModal'
import { useActiveFeatureFlags } from 'contexts/SiteConfig'
import { AssociatePaymentMethodModal } from './AssociatePaymentMethodModal'
import { SetExpiryDateModal } from './SetExpiryDateModal'
import { AssociateOrderModal } from './AssociateOrderModal'

const DefaultBarcodeCard = () => {
  const { t } = useTranslation()
  const { clientId } = useParams<{ clientId: string }>()
  const { isLoading, data: { data: customer } = {} } = useGetCustomer(clientId)

  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)
  const printRef = useRef<HTMLDivElement>(null)

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Temporary membership pass for ${customer.full_name}`,
    pageStyle: `
      @page {
        size: 85mm 50mm;
        margin: 0;
      }
    `,
  })

  const handleDownload = async () => {
    const file = await QRCode.toDataURL(`TRYBM-${clientId}`)

    // Now make an anchor tag so we can programatically click it.
    const elm = document.createElement('a')
    elm.setAttribute('href', file)
    elm.setAttribute('download', `${customer.full_name.replace(' ', '-').toLowerCase()}-qrcode.png`)
    elm.style.display = 'none'
    document.body.appendChild(elm)
    elm.click()
    document.body.removeChild(elm)
  }

  useLayoutEffect(() => {
    if (canvas) {
      QRCode.toCanvas(canvas, `TRYBM-${clientId}`, {
        margin: 0,
        scale: 3,
      })
    }
  }, [canvas, clientId])

  return (
    <>
      <div className="text-gray-500 font-medium mb-1 text-sm">Default pass</div>
      <div className="p-3 bg-violet-50 aspect-[85/50] shadow-sm rounded-md mt-1 mb-4">
        <div className="h-full relative">
          <div ref={printRef} className="print:p-3 grid grid-cols-3 h-full">
            <div className="col-span-2 flex flex-col justify-between">
              <div className="font-semibold text-xl p-3">{customer.full_name}</div>

              <div className="">
                <ClientAvatar avatarUrl={customer.avatar ? customer.avatar.url : null} />
              </div>
            </div>
            <div className="col-span-1 flex items-end">
              <canvas
                ref={setCanvas}
                className="bg-white p-1 px-2 rounded shadow-sm print:shadow-none ml-auto"
              />
            </div>
          </div>

          <div className="absolute top-0 right-0 m-1">
            <Dropdown threeDots aboveModal>
              <DropdownItem
                icon={PrinterIcon}
                label={t('clients.client_profile.manage_barcode_modal.button.print')}
                onClick={handlePrint}
                disabled={isLoading}
              />
              <DropdownItem
                icon={DocumentDownloadIcon}
                label={t('clients.client_profile.manage_barcode_modal.button.download')}
                onClick={handleDownload}
              />
            </Dropdown>
          </div>
        </div>
      </div>
    </>
  )
}

type AssociatedBarcode = any

interface AssociatedBarcodeCardProps {
  clientId: string
  barcode: AssociatedBarcode
  onAssociatePaymentMethod: () => void
  onShowExpiryModal: () => void
  onAssociateOrder: () => void
}

export const AssociatedBarcodeCard: FC<AssociatedBarcodeCardProps> = ({
  clientId,
  barcode,
  onAssociatePaymentMethod,
  onAssociateOrder,
  onShowExpiryModal,
}) => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanManageClients = can(CUSTOMERS_MANAGE)
  const notification = useNotificationContext()
  const { mutate: updateAssociatedBarcode, isLoading: isUpdatingAssociatedBarcode } =
    useUpdateAssociatedBarcode()

  const featureFlags = useActiveFeatureFlags()
  const { mutate: dissociate, isLoading: isDisassociating } = useDisassociateBarcode()

  const getPaymentMethodBadge = (barcode: AssociatedBarcode) => {
    const paymentMethod = barcode.payment_method

    if (!paymentMethod) return null

    const label = t('clients.client_profile.manage_barcode_modal.payment_method_badge.card', {
      lastFour: paymentMethod.last_4,
    })

    return (
      <div className="flex space-x-2 items-center">
        <PaymentMethodIcon size={32} type={paymentMethod.card_brand} />
        <Badge variant="info" label={label} />
      </div>
    )
  }

  const getOrderBadge = (barcode: AssociatedBarcode) => {
    const order = barcode.order

    if (!order) return null

    return (
      <div className="flex space-x-2 items-center">
        <Badge variant="success" label={`Order: ${order.order_ref}`} />
      </div>
    )
  }

  const handleDissociateWearable = (barcode: AssociatedBarcode) => {
    dissociate(
      {
        customerId: clientId,
        barcodeId: barcode.id,
      },
      {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t(
                'clients.client_profile.manage_barcode_modal.dissociate_pass.notification.title'
              )}
              description={t(
                'clients.client_profile.manage_barcode_modal.dissociate_pass.notification.description'
              )}
              variant="success"
              autoDismiss
            />
          )
        },
        onError: (e) => {
          notification.notifyApiError(e)
        },
      }
    )
  }

  const handleClearExpiryDate = (barcode: AssociatedBarcode) => {
    updateAssociatedBarcode(
      {
        customerId: clientId,
        barcodeId: barcode.id,
        updateAssociatedBarcode: {
          expires_at: null,
        },
      },
      {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t(
                'clients.client_profile.manage_barcode_modal.clear_expiry_date.notification.title'
              )}
              description={t(
                'clients.client_profile.manage_barcode_modal.clear_expiry_date.notification.description'
              )}
              variant="success"
              autoDismiss
            />
          )
        },
        onError: (response) => {
          notification.notifyApiError(response)
        },
      }
    )
  }

  const handleDisassociateOrder = (barcode) => {
    updateAssociatedBarcode(
      {
        customerId: clientId,
        barcodeId: barcode.id,
        updateAssociatedBarcode: {
          order_id: null,
        },
      },
      {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t(
                'clients.client_profile.manage_barcode_modal.disassociate_order.notification.title'
              )}
              description={t(
                'clients.client_profile.manage_barcode_modal.disassociate_order.notification.description'
              )}
              variant="success"
              autoDismiss
            />
          )
        },
        onError: (response) => {
          notification.notifyApiError(response)
        },
      }
    )
  }

  return (
    <ResourceListItem
      compact
      key={barcode.id}
      title={barcode.barcode}
      subtitle={
        <div className="space-y-1">
          <div>
            Associated{' '}
            {DateTime.fromJSDate(barcode.created_at).toLocaleString(DateTime.DATETIME_MED)}
          </div>

          {barcode.expires_at ? (
            <div>
              Expires{' '}
              {DateTime.fromJSDate(barcode.expires_at).toLocaleString(DateTime.DATETIME_MED)}
            </div>
          ) : (
            <div className="italic">Never expires</div>
          )}
          {getPaymentMethodBadge(barcode)}
          {getOrderBadge(barcode)}
        </div>
      }
      dropdownAboveModal
      dropdownItems={[
        <DropdownItem
          label={isDisassociating ? 'Dissociating...' : 'Dissociate'}
          onClick={() => handleDissociateWearable(barcode)}
          disabled={isDisassociating || !userCanManageClients}
          disabledReason={t('tooltip.missing_permission.clients_manage')}
          icon={XIcon}
        />,
        <DropdownDivider />,
        barcode.expires_at ? (
          <DropdownItem
            label={t('clients.client_profile.manage_barcode_modal.dropdown.clear_expiry_date')}
            onClick={() => handleClearExpiryDate(barcode)}
            disabled={!userCanManageClients || isUpdatingAssociatedBarcode}
            icon={TrashIcon}
          />
        ) : (
          <></>
        ),
        <DropdownItem
          label={t('clients.client_profile.manage_barcode_modal.dropdown.set_expiry_date')}
          onClick={onShowExpiryModal}
          disabled={!userCanManageClients || isUpdatingAssociatedBarcode}
          icon={CalendarIcon}
        />,
        featureFlags.includes('wearable_management_in_app') ? (
          <>
            <DropdownDivider />
            {barcode.order ? (
              <DropdownItem
                label={t('clients.client_profile.manage_barcode_modal.dropdown.disassociate_order')}
                onClick={() => handleDisassociateOrder(barcode)}
                disabled={!userCanManageClients || isUpdatingAssociatedBarcode}
                disabledReason={t('tooltip.missing_permission.clients_manage')}
                icon={TrashIcon}
              />
            ) : (
              <DropdownItem
                label={t('clients.client_profile.manage_barcode_modal.dropdown.associate_order')}
                onClick={onAssociateOrder}
                disabled={!userCanManageClients}
                disabledReason={t('tooltip.missing_permission.clients_manage')}
                icon={LinkIcon}
              />
            )}
            <DropdownDivider />
            <DropdownItem
              label={t(
                'clients.client_profile.manage_barcode_modal.dropdown.associate_payment_method'
              )}
              onClick={onAssociatePaymentMethod}
              disabled={!userCanManageClients}
              disabledReason={t('tooltip.missing_permission.clients_manage')}
              icon={PlusIcon}
            />
          </>
        ) : (
          <></>
        ),
      ]}
    />
  )
}

const ManageBarcodeScreen = () => {
  const { clientId } = useParams<{ clientId: string }>()
  const [showAssociatePaymentMethodModal, setShowAssociatePaymentMethodModal] = useState(false)
  const [showAssociateOrderModal, setShowAssociateOrderModal] = useState(false)
  const [selectedBarcode, setSelectedBarcode] = useState<AssociatedBarcode | null>(null)
  const [showSetExpiryModal, setShowSetExpiryModal] = useState(false)
  const { isLoading: isLoadingAssociatedBarcodes, data: { data: associatedBarcodes = [] } = {} } =
    useAssociatedBarcodesForClient(clientId)

  return (
    <>
      <DefaultBarcodeCard />

      <div className="text-gray-500 font-medium mb-1 text-sm">Custom associated passes</div>

      <p className="text-sm text-gray-500 mb-2">
        If you have an existing pass for this client, such as a physical membership card, you can
        associate it with this client.
      </p>

      <PaginatedMiniList
        isLoading={isLoadingAssociatedBarcodes}
        emptyMessage="No associated passes"
        rows={associatedBarcodes.map((barcode: AssociatedBarcode) => (
          <AssociatedBarcodeCard
            clientId={clientId}
            barcode={barcode}
            onAssociatePaymentMethod={() => {
              setSelectedBarcode(barcode)
              setShowAssociatePaymentMethodModal(true)
            }}
            onShowExpiryModal={() => {
              setSelectedBarcode(barcode)
              setShowSetExpiryModal(true)
            }}
            onAssociateOrder={() => {
              setSelectedBarcode(barcode)
              setShowAssociateOrderModal(true)
            }}
          />
        ))}
      />

      <SetExpiryDateModal
        clientId={clientId}
        isOpen={showSetExpiryModal}
        onClose={() => {
          setShowSetExpiryModal(false)
          setSelectedBarcode(null)
        }}
        barcode={selectedBarcode}
      />

      <AssociatePaymentMethodModal
        isOpen={showAssociatePaymentMethodModal}
        onClose={() => setShowAssociatePaymentMethodModal(false)}
        barcodeId={selectedBarcode?.id}
        clientId={clientId}
        initialPaymentMethodId={selectedBarcode?.payment_method?.id}
      />

      <AssociateOrderModal
        isOpen={showAssociateOrderModal}
        onClose={() => {
          setShowAssociateOrderModal(false)
          setSelectedBarcode(null)
        }}
        barcodeId={selectedBarcode?.id}
        initialOrderId={selectedBarcode?.order_id}
      />
    </>
  )
}

const AssociateNewBarcodeScreen = ({ onAssociated, onBack }) => {
  const { t } = useTranslation()
  const { clientId } = useParams<{ clientId: string }>()
  const notification = useNotificationContext()
  const { setActive, prefixes, suffixes } = useGlobalBarcodeListenerContext()
  const { isLoading, mutate } = useAssociateBarcode()
  const [manualEntry, setManualEntry] = useState('')
  const [openTerminalModal, setOpenTerminalModal] = useState(false)
  const featureFlags = useActiveFeatureFlags()

  useEffect(() => {
    // Whenever this component is mounted, disable the global barcode
    // listener so we can override the behaviour.
    setActive(false)

    return () => {
      setActive(true)
    }
  })

  const onAssociate: BarcodeListenerProps['onScan'] = (code) => {
    if (code.length < 4) {
      notification.notify(
        <Notification
          title={t('clients.client_profile.manage_barcode_modal.code_too_short.notification.title')}
          description={t(
            'clients.client_profile.manage_barcode_modal.code_too_short.notification.description'
          )}
          variant="danger"
          autoDismiss
        />
      )

      return
    }

    mutate([clientId, code], {
      onSuccess: () => {
        onAssociated()

        notification.notify(
          <Notification
            title={t(
              'clients.client_profile.manage_barcode_modal.associate_pass.notification.title'
            )}
            description={t(
              'clients.client_profile.manage_barcode_modal.associate_pass.notification.description'
            )}
            variant="success"
            autoDismiss
          />
        )
      },
      onError: (e) => {
        notification.notify(<ApiErrorNotification error={e} />)
      },
    })
  }

  const handleWearableAssociated = () => {
    onAssociated()
  }

  return (
    <>
      <div
        className={`${
          !isLoading ? 'animate-pulse' : ''
        } w-full h-32 rounded-md bg-gray-200 flex justify-center items-center text-sm font-medium relative`}
      >
        {!isLoading && (
          <div className="text-center">
            <div>Awaiting input</div>
            <div className="font-normal text-xs mt-1">
              Scan, swipe or tap the pass using your connected reader.
            </div>
          </div>
        )}

        {isLoading && (
          <div className="text-white text-center">
            <Spinner />
          </div>
        )}
      </div>

      <div className="my-4 text-center text-gray-500 text-xs font-medium">or</div>
      <Input
        label={t('clients.client_profile.manage_barcode_modal.input.manually_enter_code')}
        hideLabel
        placeholder={t('clients.client_profile.manage_barcode_modal.input.manually_enter_code')}
        onChange={(e) => setManualEntry(e.target.value)}
        value={manualEntry}
        className="flex-1 w-full"
      />

      <input
        type="text"
        className="opacity-0 absolute top-0 left-0"
        autoFocus
        onInput={(e) => e.preventDefault()}
      />

      {featureFlags.includes('wearable_management_in_app') && (
        <>
          <div className="mt-4">
            <Button
              label={t('clients.client_profile.manage_barcode_modal.scan_from_reader_button')}
              fullWidth
              onClick={() => setOpenTerminalModal(true)}
              disabled={isLoading}
            />
          </div>

          <SelectTerminalModal
            customerId={clientId}
            isOpen={openTerminalModal}
            onClose={() => setOpenTerminalModal(false)}
            onComplete={handleWearableAssociated}
            isWearableAssociation
          />
        </>
      )}

      <BarcodeListener prefixCodes={prefixes} suffixCodes={suffixes} onScan={onAssociate} />

      <ModalButtons>
        <Button
          label={t('clients.client_profile.manage_barcode_modal.button.associate')}
          variant="primary"
          onClick={() => onAssociate(manualEntry)}
        />
        <Button
          label={t('clients.client_profile.manage_barcode_modal.button.back')}
          onClick={() => onBack()}
        />
      </ModalButtons>
    </>
  )
}

export const ManageBarcodeModal = ({ onClose, isOpen }) => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanManageClients = can(CUSTOMERS_MANAGE)
  const [screen, setScreen] = useState(0)
  const [hasAssociated, setHasAssociated] = useState(false)

  const handleAssociated = () => {
    setScreen(0)
    setHasAssociated(true)
  }

  useEffect(() => {
    return () => {
      setScreen(0)
      setHasAssociated(false)
    }
  }, [])

  return (
    <Modal
      isOpen={isOpen}
      showHideIcon={false}
      onClose={() => {}} // Don't do anything to fix bug with nested dropdowns
      title={t('clients.client_profile.manage_barcode_modal.title')}
      size="sm"
    >
      {screen === 0 && (
        <>
          <ManageBarcodeScreen />
          <ModalButtons>
            <Button
              label={
                hasAssociated
                  ? t('clients.client_profile.manage_barcode_modal.button.associate_another_pass')
                  : t('clients.client_profile.manage_barcode_modal.button.associate_new_pass')
              }
              onClick={() => setScreen(1)}
              variant="primary"
              disabled={!userCanManageClients}
              disabledReason={t('tooltip.missing_permission.clients_manage')}
            />
            <Button
              label={
                hasAssociated
                  ? t('clients.client_profile.manage_barcode_modal.button.done')
                  : t('clients.client_profile.manage_barcode_modal.button.close')
              }
              onClick={onClose}
            />
          </ModalButtons>
        </>
      )}

      {screen === 1 && (
        <>
          <AssociateNewBarcodeScreen onAssociated={handleAssociated} onBack={() => setScreen(0)} />
        </>
      )}
    </Modal>
  )
}
